import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { MarketTable } from './MarketTable';
import { StockMarketTable } from './StockMarketTable';

type MarketTab = 'crypto' | 'stocks';

const TABS: { id: MarketTab; label: string }[] = [
  { id: 'crypto', label: 'Crypto' },
  { id: 'stocks', label: 'Stocks & ETFs' },
];

/**
 * Market tab content: an underline sub-tab row (identical house style to the
 * Strategy / Tax tabs) over two panels. Both panels stay MOUNTED and are toggled
 * with `display` so each table's search box and scroll position survive switching.
 * The active sub-tab persists in the URL (?sub=…) so a refresh keeps it.
 */
export function MarketView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const subParam = searchParams.get('sub');
  const tab: MarketTab = subParam === 'stocks' ? 'stocks' : 'crypto';

  const setTab = (id: MarketTab) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sub', id);
        return next;
      },
      { replace: true }
    );

  return (
    <div>
      {/* Underline sub-tab row */}
      <div
        className="flex gap-1 overflow-x-auto"
        style={{ borderBottom: '0.5px solid var(--tab-row-border)' }}
      >
        {TABS.map((t) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className="whitespace-nowrap transition-colors"
              style={{
                background: 'transparent',
                padding: '10px 14px',
                fontSize: '13px',
                borderBottom: isActive ? '2px solid var(--tab-accent)' : '2px solid transparent',
                marginBottom: '-0.5px',
                color: isActive ? 'var(--tab-text-active)' : 'var(--tab-text-inactive)',
                fontWeight: isActive ? 500 : 400,
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-hover)'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-inactive)'; }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panels — kept mounted, toggled via display to preserve state */}
      <div className="pt-6">
        <div style={{ display: tab === 'crypto' ? 'block' : 'none' }}>
          <MarketTable />
        </div>
        <div style={{ display: tab === 'stocks' ? 'block' : 'none' }}>
          <StockMarketTable />
        </div>
      </div>
    </div>
  );
}
